import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FiMic, FiPause, FiSquare, FiX, FiPlay } from 'react-icons/fi';
import { BootstrapToolTip } from '@/_elements/ToolTip';

const AudioRecorder = ({
    onRecordingComplete,
    onRecordingStart = () => {},
    maxDuration = 300,
    fileName = 'audio-recording.webm',
    showTimer = true,
    compact = false,
    handleClose,
}) => {
    const [status, setStatus] = useState('inactive');
    const [recordingTime, setRecordingTime] = useState(0);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const streamRef = useRef(null);
    const isCancelledRef = useRef(false);

    const fixWebmDuration = (blob, duration) => {
        return new Promise((resolve) => {
            try {
                const reader = new FileReader();
                reader.onload = () => {
                    const arrayBuffer = reader.result;

                    const durationBytes = new Uint8Array(8);
                    const durationView = new DataView(durationBytes.buffer);
                    durationView.setFloat64(0, duration * 1000, false);

                    let found = false;
                    const uint8Array = new Uint8Array(arrayBuffer);

                    for (let i = 0; i < uint8Array.length - 10; i++) {
                        if (uint8Array[i] === 0x44 && uint8Array[i + 1] === 0x89) {
                            for (let j = 0; j < 8; j++) {
                                uint8Array[i + 2 + j] = durationBytes[j];
                            }
                            found = true;
                            break;
                        }
                    }

                    if (found) {
                        const fixedBlob = new Blob([uint8Array], { type: blob.type });
                        resolve(fixedBlob);
                    } else {
                        console.warn('Could not find duration segment in WebM file');
                        resolve(blob);
                    }
                };
                reader.readAsArrayBuffer(blob);
            } catch (error) {
                console.warn('WebM duration fix failed:', error);
                resolve(blob);
            }
        });
    };

    const getBestMimeType = () => {
        const types = ['audio/mp4', 'audio/mpeg', 'audio/wav', 'audio/webm;codecs=opus', 'audio/webm'];

        return types.find((type) => MediaRecorder.isTypeSupported(type)) || 'audio/webm';
    };

    const getAudioDuration = async (blob) => {
        const audioElementDuration = await new Promise((resolve) => {
            const audio = new Audio();
            const url = URL.createObjectURL(blob);

            const cleanup = () => {
                URL.revokeObjectURL(url);
                audio.removeEventListener('loadedmetadata', onLoadedMetadata);
                audio.removeEventListener('error', onError);
            };

            const onLoadedMetadata = () => {
                cleanup();
                resolve(audio.duration);
            };

            const onError = () => {
                cleanup();
                resolve(null);
            };

            audio.addEventListener('loadedmetadata', onLoadedMetadata);
            audio.addEventListener('error', onError);
            audio.src = url;

            setTimeout(() => {
                cleanup();
                resolve(null);
            }, 5000);
        });

        if (audioElementDuration && !isNaN(audioElementDuration) && audioElementDuration > 0) {
            return audioElementDuration;
        }

        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const arrayBuffer = await blob.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

            if (audioBuffer.duration && !isNaN(audioBuffer.duration) && audioBuffer.duration > 0) {
                return audioBuffer.duration;
            }
        } catch (error) {
            console.warn('Web Audio API duration detection failed:', error);
        }

        console.warn('Using fallback duration based on recording time');
        return recordingTime;
    };

    const handleRecordingStop = async () => {
        if (isCancelledRef.current) {
            // Reset flag and skip processing
            isCancelledRef.current = false;
            return;
        }

        setStatus('processing');

        try {
            const mimeType = getBestMimeType();
            let audioBlob = new Blob(audioChunksRef.current, {
                type: mimeType,
            });

            const duration = await getAudioDuration(audioBlob);

            if (mimeType.includes('webm')) {
                console.log('Fixing WebM duration metadata...');
                audioBlob = await fixWebmDuration(audioBlob, duration);
            }

            let fileExtension = '.webm';
            if (mimeType.includes('mp4')) {
                fileExtension = '.mp4';
            } else if (mimeType.includes('wav')) {
                fileExtension = '.wav';
            } else if (mimeType.includes('mpeg')) {
                fileExtension = '.mp3';
            }

            const finalFileName = fileName.includes('.')
                ? fileName.replace(/\.[^/.]+$/, fileExtension)
                : fileName + fileExtension;

            const file = new File([audioBlob], finalFileName, {
                type: mimeType,
                lastModified: Date.now(),
            });

            Object.defineProperty(file, 'duration', {
                value: duration,
                writable: false,
                enumerable: true,
            });

            console.log(`Audio file created: ${finalFileName}, Duration: ${duration.toFixed(2)}s, Type: ${mimeType}`);

            if (onRecordingComplete) {
                onRecordingComplete(file);
            }
        } catch (error) {
            console.error('Error processing audio:', error);

            const fallbackBlob = new Blob(audioChunksRef.current, {
                type: getBestMimeType(),
            });

            const file = new File([fallbackBlob], fileName, {
                type: fallbackBlob.type,
                lastModified: Date.now(),
            });

            Object.defineProperty(file, 'duration', {
                value: recordingTime,
                writable: false,
                enumerable: true,
            });

            if (onRecordingComplete) {
                onRecordingComplete(file);
            }
        } finally {
            if (handleClose) {
                handleClose();
            }

            setStatus('inactive');
        }
    };

    const startRecording = async () => {
        try {
            onRecordingStart();
            setStatus('processing');
            setRecordingTime(0);
            audioChunksRef.current = [];

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 48000,
                    channelCount: 1,
                },
            });
            streamRef.current = stream;

            const mimeType = getBestMimeType();
            console.log('Recording with MIME type:', mimeType);

            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: mimeType,
                audioBitsPerSecond: 128000,
            });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = handleRecordingStop;

            mediaRecorder.start(1000);
            setStatus('recording');

            timerRef.current = setInterval(() => {
                setRecordingTime((prevTime) => {
                    const newTime = prevTime + 1;
                    if (newTime >= maxDuration) {
                        stopRecording();
                        return maxDuration;
                    }
                    return newTime;
                });
            }, 1000);
        } catch (error) {
            console.error('Error accessing microphone:', error);
            setStatus('inactive');
            alert('Could not access microphone. Please check your permissions.');
        }
    };

    const pauseRecording = () => {
        if (mediaRecorderRef.current && status === 'recording') {
            mediaRecorderRef.current.pause();
            setStatus('paused');
            clearInterval(timerRef.current);
        }
    };

    const resumeRecording = () => {
        if (mediaRecorderRef.current && status === 'paused') {
            mediaRecorderRef.current.resume();
            setStatus('recording');

            timerRef.current = setInterval(() => {
                setRecordingTime((prevTime) => {
                    const newTime = prevTime + 1;
                    if (newTime >= maxDuration) {
                        stopRecording();
                        return maxDuration;
                    }
                    return newTime;
                });
            }, 1000);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && (status === 'recording' || status === 'paused')) {
            setStatus('processing');
            mediaRecorderRef.current.stop();
            streamRef.current.getTracks().forEach((track) => track.stop());
            clearInterval(timerRef.current);
        }
    };

    const cancelRecording = () => {
        if (mediaRecorderRef.current && (status === 'recording' || status === 'paused')) {
            isCancelledRef.current = true;
            mediaRecorderRef.current.stop();
            streamRef.current.getTracks().forEach((track) => track.stop());
            clearInterval(timerRef.current);
            setStatus('inactive');
            audioChunksRef.current = [];
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }
        };
    }, []);

    const renderControls = () => {
        const iconProps = { size: 20, strokeWidth: 2 };

        const renderButton = (className, onClick, Icon, label, title) => (
            <BootstrapToolTip text={title}>
                <button className={`audio-recorder-btn ${className}`} onClick={onClick}>
                    <Icon {...iconProps} />
                    {!compact && <span>{label}</span>}
                </button>
            </BootstrapToolTip>
        );

        switch (status) {
            case 'inactive':
                return renderButton('start-btn', startRecording, FiMic, 'Record', 'Start recording');

            case 'recording':
                return (
                    <div className="audio-recorder-controls">
                        {renderButton('pause-btn', pauseRecording, FiPause, 'Pause', 'Pause recording')}
                        {renderButton('stop-btn', stopRecording, FiSquare, 'Done', 'Stop and save recording')}
                        {renderButton('cancel-btn', cancelRecording, FiX, 'Cancel', 'Cancel recording')}
                    </div>
                );

            case 'paused':
                return (
                    <div className="audio-recorder-controls">
                        {renderButton('resume-btn', resumeRecording, FiPlay, 'Resume', 'Resume recording')}
                        {renderButton('stop-btn', stopRecording, FiSquare, 'Done', 'Stop and save recording')}
                        {renderButton('cancel-btn', cancelRecording, FiX, 'Cancel', 'Cancel recording')}
                    </div>
                );

            case 'processing':
                return (
                    <div className="audio-recorder-processing">
                        <div className="audio-recorder-spinner" />
                        <span>Processing...</span>
                    </div>
                );

            default:
                return null;
        }
    };
    return (
        <div className={`audio-recorder ${compact ? 'compact' : ''}`}>
            {renderControls()}

            {(status === 'recording' || status === 'paused') && showTimer && (
                <div className="audio-recorder-timer">
                    <div className={`recording-indicator ${status === 'paused' ? 'paused' : ''}`}></div>
                    <span>{formatTime(recordingTime)}</span>
                </div>
            )}

            <style jsx>{`
                .audio-recorder {
                    display: inline-flex;
                    align-items: center;
                    gap: 12px;
                    font-family: sans-serif;
                }

                .audio-recorder.compact {
                    flex-direction: column;
                    align-items: center;
                }

                .audio-recorder-controls {
                    display: flex;
                    gap: 8px;
                }

                .audio-recorder-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 8px 12px;
                    border-radius: 4px;
                    border: none;
                    font-size: 14px;
                    cursor: pointer;
                    color: white;
                    transition: all 0.2s ease;
                }

                .compact .audio-recorder-btn {
                    width: 36px;
                    height: 36px;
                    padding: 0;
                    border-radius: 50%;
                }

                .start-btn {
                    background-color: #50aeb0;
                }

                .pause-btn {
                    background-color: #ff9800;
                }

                .resume-btn {
                    background-color: #2196f3;
                }

                .stop-btn {
                    background-color: #f44336;
                }

                .cancel-btn {
                    background-color: #757575;
                }

                .audio-recorder-btn:hover {
                    opacity: 0.9;
                }

                .audio-recorder-timer {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 14px;
                    color: #555;
                }

                .recording-indicator {
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background-color: #f44336;
                    animation: pulse 1.5s infinite;
                }

                .recording-indicator.paused {
                    background-color: #ff9800;
                    animation: none;
                }

                @keyframes pulse {
                    0% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.3;
                    }
                    100% {
                        opacity: 1;
                    }
                }

                .audio-recorder-processing {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #555;
                    font-size: 14px;
                }

                .audio-recorder-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid #ddd;
                    border-top-color: #555;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </div>
    );
};

AudioRecorder.propTypes = {
    onRecordingComplete: PropTypes.func.isRequired,
    maxDuration: PropTypes.number,
    fileName: PropTypes.string,
    showTimer: PropTypes.bool,
    compact: PropTypes.bool,
};

export default AudioRecorder;
